
import { Dealer } from "@/types/dealer";
import { Badge } from "@/components/ui/badge";
import { Euro, MapPin, ShieldCheck } from "lucide-react";
import { calculateDealerPrice, getPurityMultiplier, type GoldPurity, type SilverPurity, Purity } from "@/lib/pricing";
import { DealerRating } from "@/components/dealer-card/DealerRating";
import { DEFAULT_GOLD_PURITY } from "@/lib/constants";
import { formatPurity } from "@/lib/formatPurity";
import { useDealerUtils } from "@/hooks/dealers/useDealerUtils";
import { cn } from "@/lib/utils";

interface RecommendedDealersListProps {
  dealers: Dealer[];
  marketPrice: number;
  goldPurity: string;
  metal: "gold" | "silver";
  onDealerClick: (dealerId: string) => void;
}

export function RecommendedDealersList({
  dealers,
  marketPrice,
  goldPurity,
  metal,
  onDealerClick
}: RecommendedDealersListProps) {
  const { createDealerCanonicalPath } = useDealerUtils();
  
  if (!dealers || dealers.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Nessun compro oro consigliato disponibile.</p>
      </div>
    );
  }
  
  // Use provided goldPurity or default
  const effectiveGoldPurity = (goldPurity || DEFAULT_GOLD_PURITY) as Purity;
  
  // Get Italian metal name for display
  const metalDisplayName = metal === "gold" ? "Oro" : "Argento";
  
  // Format purity for display
  const formattedPurity = formatPurity(metal, effectiveGoldPurity);
  
  // Handle dealer click
  const handleDealerClick = (dealer: Dealer) => {
    onDealerClick(dealer.id);
  };

  return (
    <div className="divide-y divide-border/40">
      {dealers.map((dealer) => {
        // Ensure we have numeric values for calculations
        const numericRating = dealer.rating;
        const numericRatingCount = dealer.rating_count || 0; // Get rating_count
        const numericBuySpread = dealer.buySpread ?? -5;
        
        // Apply purity multiplier to market price - This is the RAW market price
        // We need to apply the purity multiplier to get the correct price for the selected purity
        const marketPriceWithPurity = marketPrice * getPurityMultiplier(
          metal === "gold" ? "ORO" : "ARGENTO",
          effectiveGoldPurity
        );
        
        // Calculate buying price using purity-adjusted market price
        const buyPrice = marketPriceWithPurity > 0 && dealer.activated
          ? calculateDealerPrice(marketPriceWithPurity, numericBuySpread) || 0
          : 0;
        
        return (
          <div
            key={dealer.id}
            className="p-4 group cursor-pointer transition-all hover:bg-accent/30 relative"
          >
            <div 
              className="cursor-pointer"
              onClick={() => handleDealerClick(dealer)}
            >
              <div className="flex justify-between items-center mb-1">
                <h4 className="font-medium group-hover:text-metal transition-colors pr-8">{dealer.name}</h4>
                {dealer.activated && (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    Verificato
                  </Badge>
                )}
              </div>
              
              <div className="flex items-center text-xs text-muted-foreground mb-2">
                <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">{dealer.address}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <DealerRating 
                  rating={numericRating} 
                  rating_count={numericRatingCount} 
                  size="sm"
                />
                
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline" className="text-xs">
                    <Euro className="h-3 w-3 mr-1" />
                    {numericBuySpread > 0 ? "+" : ""}{numericBuySpread.toFixed(1)}%
                  </Badge>
                  
                  <Badge 
                    variant={metal === "gold" ? "gold" : "silver"} 
                    className="text-xs"
                  >
                    {buyPrice.toFixed(2)} €/g
                    <span className="ml-1 text-[10px] opacity-80">
                      {metalDisplayName} {formattedPurity}
                    </span>
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
